import { motion, useInView, useScroll, useTransform } from 'framer-motion';
import { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import WhatsAppButton from '../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../components/feature/ScrollToTopButton';

const ServiciosPage = () => {
  const heroRef = useRef(null);

  // Parallax effect for hero
  const { scrollY } = useScroll();
  const heroImageY = useTransform(scrollY, [0, 500], [0, 120]);
  const heroImageScale = useTransform(scrollY, [0, 500], [1, 1.2]);

  // Set document metadata
  useEffect(() => {
    document.title = 'Servicios de Pladur en Terrassa | Tabiques, Techos y Aislamientos | PLADUREX';
    
    let metaDescription = document.querySelector('meta[name="description"]');
    if (!metaDescription) {
      metaDescription = document.createElement('meta');
      metaDescription.setAttribute('name', 'description');
      document.head.appendChild(metaDescription);
    }
    metaDescription.setAttribute('content', 'Servicios profesionales de pladur en Terrassa: tabiques, trasdosados, falsos techos, aislamientos y reformas integrales en Terrassa');

    // Add canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/servicios`);

    // Add JSON-LD schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Servicios de Instalación de Pladur en Terrassa",
      "description": "Servicios profesionales de instalación de pladur: tabiques, trasdosados, falsos techos, aislamientos acústicos y térmicos, pladur antihumedad y reformas integrales",
      "url": `${import.meta.env.VITE_SITE_URL}/servicios`,
      "provider": {
        "@type": "LocalBusiness",
        "name": "PLADUREX",
        "telephone": "+34629501941",
        "address": {
          "@type": "PostalAddress",
          "addressLocality": "Terrassa",
          "addressRegion": "Barcelona",
          "addressCountry": "ES"
        }
      },
      "areaServed": [
        {
          "@type": "City",
          "name": "Terrassa"
        },
        {
          "@type": "AdministrativeArea",
          "name": "Vallès Occidental"
        }
      ],
      "hasOfferCatalog": {
        "@type": "OfferCatalog",
        "name": "Servicios de Pladur",
        "itemListElement": [
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Tabiques de Pladur",
              "description": "Instalación de tabiques divisorios con pladur para redistribuir espacios"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Trasdosados",
              "description": "Trasdosados directos y autoportantes con aislamiento térmico y acústico"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Falsos Techos",
              "description": "Instalación de falsos techos continuos, registrables y desmontables"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Aislamiento Acústico",
              "description": "Soluciones profesionales de insonorización y aislamiento acústico"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Aislamiento Térmico",
              "description": "Mejora de eficiencia energética con aislamiento térmico profesional"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Pladur Antihumedad",
              "description": "Instalación de placas hidrófugas para baños, cocinas y zonas húmedas"
            }
          },
          {
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": "Reformas Integrales con Pladur",
              "description": "Reformas completas de viviendas, oficinas y locales comerciales"
            }
          }
        ]
      },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": "Inicio",
            "item": `${import.meta.env.VITE_SITE_URL}/`
          },
          {
            "@type": "ListItem",
            "position": 2,
            "name": "Servicios",
            "item": `${import.meta.env.VITE_SITE_URL}/servicios`
          }
        ]
      }
    });
    document.head.appendChild(script);

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, []);

  const services = [
    {
      title: 'Tabiques de Pladur',
      description: 'Creamos divisiones interiores con pladur para redistribuir espacios de forma rápida y limpia. Ideal para separar ambientes, crear nuevas habitaciones o despachos sin necesidad de obra húmeda.',
      image: 'https://public.readdy.ai/ai/img_res/aeeb2a8b67d87a101a1531a1785efd70.jpg',
      includes: [
        'Perfilería metálica de calidad',
        'Placas de pladur estándar o especiales',
        'Aislamiento acústico opcional',
        'Acabado listo para pintar',
        'Instalación de puertas si es necesario'
      ],
      idealFor: [
        'Crear nuevas habitaciones',
        'Separar zonas en oficinas',
        'Dividir espacios diáfanos',
        'Reformas de distribución'
      ],
      priceFactors: [
        'Metros cuadrados de tabique',
        'Altura del techo',
        'Tipo de placa (estándar, antihumedad, ignífuga)',
        'Nivel de aislamiento acústico',
        'Instalación de puertas o pasos'
      ]
    },
    {
      title: 'Trasdosados',
      description: 'Mejoramos paredes existentes con trasdosados de pladur, tanto directos como autoportantes. Perfectos para mejorar el aislamiento térmico y acústico, ocultar instalaciones o corregir irregularidades.',
      image: 'https://readdy.ai/api/search-image?query=drywall%20wall%20lining%20with%20insulation%20in%20modern%20interior%20construction&width=600&height=400&seq=servicio-trasdosados-001&orientation=landscape',
      includes: [
        'Trasdosado directo o autoportante',
        'Aislamiento térmico con lana mineral',
        'Aislamiento acústico',
        'Placas antihumedad si es necesario',
        'Acabado perfecto listo para decorar'
      ],
      idealFor: [
        'Mejorar aislamiento de fachadas',
        'Reducir ruido de vecinos',
        'Ocultar tuberías e instalaciones',
        'Corregir paredes irregulares'
      ],
      priceFactors: [
        'Metros cuadrados de pared',
        'Tipo de trasdosado (directo o autoportante)',
        'Grosor y tipo de aislamiento',
        'Estado de la pared existente',
        'Complejidad de instalaciones a ocultar'
      ]
    },
    {
      title: 'Falsos Techos',
      description: 'Instalamos falsos techos de pladur continuos, registrables o desmontables. Ideales para ocultar instalaciones, mejorar la estética, integrar iluminación o mejorar el aislamiento acústico.',
      image: 'https://readdy.ai/api/search-image?query=suspended%20ceiling%20drywall%20with%20integrated%20lighting%20in%20contemporary%20interior&width=600&height=400&seq=servicio-techos-001&orientation=landscape',
      includes: [
        'Estructura metálica resistente',
        'Placas de pladur para techos',
        'Aislamiento acústico opcional',
        'Preparación para iluminación',
        'Acabado continuo o registrable'
      ],
      idealFor: [
        'Ocultar vigas e instalaciones',
        'Integrar iluminación LED',
        'Mejorar acústica de espacios',
        'Reducir altura de techos',
        'Modernizar estancias'
      ],
      priceFactors: [
        'Metros cuadrados de techo',
        'Altura de la estancia',
        'Tipo de techo (continuo o registrable)',
        'Complejidad de la iluminación',
        'Nivel de acabado requerido'
      ]
    },
    {
      title: 'Aislamiento Acústico',
      description: 'Soluciones profesionales de aislamiento acústico e insonorización con pladur y materiales específicos. Reducimos significativamente el ruido entre estancias o del exterior.',
      image: 'https://readdy.ai/api/search-image?query=acoustic%20insulation%20with%20mineral%20wool%20in%20wall%20construction%20soundproofing&width=600&height=400&seq=servicio-acustico-001&orientation=landscape',
      includes: [
        'Lana de roca de alta densidad',
        'Placas de pladur acústicas',
        'Sistema de doble placa',
        'Sellado de juntas acústico',
        'Tratamiento de puntos críticos'
      ],
      idealFor: [
        'Reducir ruido de vecinos',
        'Insonorizar salas de música',
        'Mejorar confort en dormitorios',
        'Aislar oficinas y despachos',
        'Crear espacios silenciosos'
      ],
      priceFactors: [
        'Nivel de aislamiento requerido (dB)',
        'Superficie a tratar',
        'Tipo y grosor de aislante',
        'Sistema de doble o triple placa',
        'Tratamiento de suelos y techos'
      ]
    },
    {
      title: 'Aislamiento Térmico',
      description: 'Mejoramos la eficiencia energética de tu vivienda u oficina con sistemas de aislamiento térmico. Reducimos el consumo de calefacción y aire acondicionado.',
      image: 'https://readdy.ai/api/search-image?query=thermal%20insulation%20with%20mineral%20wool%20in%20wall%20construction%20energy%20efficiency&width=600&height=400&seq=servicio-termico-001&orientation=landscape',
      includes: [
        'Aislamiento con lana mineral',
        'Trasdosados con cámara de aire',
        'Placas de pladur estándar',
        'Sellado de puentes térmicos',
        'Mejora de eficiencia energética'
      ],
      idealFor: [
        'Reducir consumo energético',
        'Mejorar confort térmico',
        'Aislar fachadas frías',
        'Reformas de eficiencia',
        'Cumplir normativa energética'
      ],
      priceFactors: [
        'Superficie a aislar',
        'Grosor del aislamiento',
        'Tipo de material aislante',
        'Estado de paredes existentes',
        'Complejidad de la instalación'
      ]
    },
    {
      title: 'Pladur Antihumedad',
      description: 'Instalamos placas de pladur antihumedad (hidrófugas) específicas para zonas húmedas como baños, cocinas y lavaderos. Resistentes a la humedad y perfectas para alicatar.',
      image: 'https://readdy.ai/api/search-image?query=green%20moisture%20resistant%20drywall%20installation%20in%20modern%20bathroom%20wet%20areas&width=600&height=400&seq=servicio-antihumedad-001&orientation=landscape',
      includes: [
        'Placas de pladur hidrófugas (verdes)',
        'Tratamiento de juntas especial',
        'Preparación para alicatado',
        'Resistencia a ambientes húmedos',
        'Garantía en zonas de agua'
      ],
      idealFor: [
        'Baños completos',
        'Cocinas y office',
        'Lavaderos y zonas de agua',
        'Trasdosados en sótanos',
        'Cualquier zona con humedad'
      ],
      priceFactors: [
        'Metros cuadrados de zona húmeda',
        'Complejidad de la instalación',
        'Necesidad de impermeabilización adicional',
        'Preparación para alicatado',
        'Tratamientos especiales'
      ]
    },
    {
      title: 'Reformas Integrales con Pladur',
      description: 'Reformas completas de viviendas, oficinas y locales utilizando sistemas de pladur. Redistribuimos espacios, mejoramos aislamientos y modernizamos interiores de forma rápida y limpia.',
      image: 'https://readdy.ai/api/search-image?query=complete%20interior%20renovation%20with%20drywall%20partitions%20and%20ceiling%20modern%20space&width=600&height=400&seq=servicio-reformas-001&orientation=landscape',
      includes: [
        'Proyecto de redistribución',
        'Tabiques y trasdosados',
        'Falsos techos',
        'Aislamientos completos',
        'Coordinación con otros gremios',
        'Acabados listos para decorar'
      ],
      idealFor: [
        'Reformas de viviendas completas',
        'Adaptación de oficinas',
        'Renovación de locales comerciales',
        'Cambios de distribución',
        'Modernización de espacios'
      ],
      priceFactors: [
        'Superficie total a reformar',
        'Complejidad del proyecto',
        'Número de estancias afectadas',
        'Nivel de acabados',
        'Coordinación con otros trabajos',
        'Plazos de ejecución'
      ]
    }
  ];

  return (
    <div className="bg-deep-green min-h-screen">
      <Header />
      <WhatsAppButton />
      <ScrollToTopButton />

      {/* Hero Section */}
      <section ref={heroRef} className="relative h-[40vh] min-h-[400px] flex items-center justify-center overflow-hidden pt-20">
        <motion.div 
          className="absolute inset-0"
          style={{ y: heroImageY }}
        >
          <motion.img
            src="https://readdy.ai/api/search-image?query=professional%20drywall%20services%20and%20construction%20tools%20in%20modern%20workspace&width=1400&height=500&seq=servicios-hero-001&orientation=landscape"
            alt="Servicios de Pladur"
            className="w-full h-[120%] object-cover object-top"
            style={{ scale: heroImageScale }}
          />
          <div className="absolute inset-0 bg-deep-green/70"></div>
        </motion.div>

        <div className="relative z-10 text-center px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <nav className="text-sm text-gray-300 mb-4">
              <Link to="/" className="hover:text-primary-yellow transition-colors">Inicio</Link>
              <span className="mx-2">&gt;</span>
              <span className="text-primary-yellow">Servicios</span>
            </nav>
            <h1 className="font-display text-5xl lg:text-6xl font-extrabold text-text-light">
              Servicios de Pladur en Terrassa
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="space-y-20">
            {services.map((service, index) => (
              <ServiceBlock key={index} service={service} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-b from-card-green to-black">
        <div className="max-w-[900px] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-display text-4xl lg:text-5xl font-bold text-text-light mb-4">
              ¿Necesitas alguno de estos servicios?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Contacta con nosotros y te asesoramos sin compromiso
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href="tel:+34629501941"
                className="px-10 py-5 bg-primary-yellow text-deep-green font-bold text-lg rounded-lg hover:shadow-[0_0_40px_rgba(255,212,0,0.6)] hover:-translate-y-1 transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl"></i>
                Llamar Ahora
              </a>
              <Link
                to="/contacto"
                className="px-10 py-5 border-2 border-primary-yellow text-primary-yellow font-bold text-lg rounded-lg hover:bg-primary-yellow hover:text-deep-green transition-all duration-300 flex items-center gap-3 whitespace-nowrap"
              >
                <i className="ri-mail-line text-xl"></i>
                Solicitar Presupuesto
              </Link>
            </div>
            
            {/* Additional links */}
            <div className="mt-8 flex flex-wrap justify-center gap-6 text-sm">
              <Link
                to="/faq"
                className="text-gray-300 hover:text-primary-yellow transition-colors flex items-center gap-2"
              >
                <i className="ri-question-line"></i>
                Preguntas frecuentes sobre pladur
              </Link>
              <Link
                to="/empresa"
                className="text-gray-300 hover:text-primary-yellow transition-colors flex items-center gap-2"
              >
                <i className="ri-building-line"></i>
                Conoce nuestra empresa
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

const ServiceBlock = ({ service, index }: { service: any; index: number }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.2 });
  const [isPriceOpen, setIsPriceOpen] = useState(false);

  const isEven = index % 2 === 0;

  // Staggered animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2
      }
    }
  };

  const imageVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      x: isEven ? -60 : 60,
      rotateY: isEven ? -15 : 15
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      x: 0,
      rotateY: 0,
      transition: {
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };

  const contentVariants = {
    hidden: { 
      opacity: 0, 
      x: isEven ? 60 : -60 
    },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: {
        duration: 0.7,
        ease: "easeOut"
      }
    }
  };

  const listItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={containerVariants}
      className={`grid lg:grid-cols-2 gap-12 items-center ${isEven ? '' : 'lg:grid-flow-dense'}`}
    >
      {/* Image */}
      <motion.div 
        className={isEven ? '' : 'lg:col-start-2'}
        variants={imageVariants}
        style={{ transformPerspective: 1000 }}
      >
        <motion.div 
          className="relative rounded-xl overflow-hidden shadow-2xl"
          whileHover={{ scale: 1.03, rotateY: isEven ? 3 : -3 }}
          transition={{ duration: 0.4 }}
        >
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-[400px] object-cover object-top"
          />
          {/* Overlay gradient animation */}
          <motion.div 
            className="absolute inset-0 bg-gradient-to-t from-deep-green/60 via-transparent to-transparent"
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
          />
          {/* Service number badge */}
          <motion.div
            className="absolute top-4 left-4 w-12 h-12 bg-primary-yellow rounded-full flex items-center justify-center"
            initial={{ scale: 0, rotate: -180 }}
            animate={inView ? { scale: 1, rotate: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.5, type: "spring", stiffness: 200 }}
          >
            <span className="text-deep-green font-bold text-lg">{index + 1}</span>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* Content */}
      <motion.div 
        className={isEven ? '' : 'lg:col-start-1 lg:row-start-1'}
        variants={contentVariants}
      >
        <motion.h2 
          className="font-display text-4xl font-bold text-text-light mb-4"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          {service.title}
        </motion.h2>
        <motion.div
          className="w-16 h-1 bg-primary-yellow mb-4"
          initial={{ width: 0 }}
          animate={inView ? { width: 64 } : {}}
          transition={{ duration: 0.6, delay: 0.4 }}
        />
        <motion.p 
          className="text-gray-300 leading-relaxed mb-6 text-[17px]"
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
        >
          {service.description}
        </motion.p>

        {/* Includes */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.6 }}
        >
          <h4 className="text-xl font-bold text-primary-yellow mb-3 flex items-center gap-2">
            <i className="ri-checkbox-circle-line"></i>
            Incluye:
          </h4>
          <ul className="space-y-2">
            {service.includes.map((item: string, idx: number) => (
              <motion.li 
                key={idx} 
                className="flex items-start gap-2 text-gray-300"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 0.7 + idx * 0.06 }}
              >
                <motion.i 
                  className="ri-arrow-right-s-line text-accent-lime mt-0.5"
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.3, delay: 0.75 + idx * 0.06, type: "spring" }}
                />
                <span className="text-sm">{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Ideal For */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.9 }}
        >
          <h4 className="text-xl font-bold text-primary-yellow mb-3 flex items-center gap-2">
            <i className="ri-lightbulb-line"></i>
            Ideal para:
          </h4>
          <ul className="space-y-2">
            {service.idealFor.map((item: string, idx: number) => (
              <motion.li 
                key={idx} 
                className="flex items-start gap-2 text-gray-300"
                initial={{ opacity: 0, x: -20 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.4, delay: 1.0 + idx * 0.06 }}
              >
                <motion.i 
                  className="ri-arrow-right-s-line text-accent-lime mt-0.5"
                  initial={{ scale: 0 }}
                  animate={inView ? { scale: 1 } : {}}
                  transition={{ duration: 0.3, delay: 1.05 + idx * 0.06, type: "spring" }}
                />
                <span className="text-sm">{item}</span>
              </motion.li>
            ))}
          </ul>
        </motion.div>

        {/* Price Factors Accordion */}
        <motion.div 
          className="bg-card-green rounded-lg overflow-hidden mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1.2 }}
        >
          <button
            onClick={() => setIsPriceOpen(!isPriceOpen)}
            className="w-full px-6 py-4 flex items-center justify-between text-left cursor-pointer hover:bg-card-green/80 transition-colors"
          >
            <h4 className="text-lg font-bold text-text-light flex items-center gap-2">
              <i className="ri-money-euro-circle-line text-primary-yellow"></i>
              Factores que afectan al precio
            </h4>
            <motion.i 
              className="ri-arrow-down-s-line text-primary-yellow text-2xl"
              animate={{ rotate: isPriceOpen ? 180 : 0 }}
              transition={{ duration: 0.3 }}
            />
          </button>
          <motion.div
            initial={false}
            animate={{ height: isPriceOpen ? 'auto' : 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-6 pb-4">
              <ul className="space-y-2">
                {service.priceFactors.map((factor: string, idx: number) => (
                  <motion.li 
                    key={idx} 
                    className="flex items-start gap-2 text-gray-300"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isPriceOpen ? { opacity: 1, x: 0 } : {}}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                  >
                    <i className="ri-price-tag-3-line text-accent-lime mt-0.5"></i>
                    <span className="text-sm">{factor}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
          </motion.div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 1.3 }}
        >
          <Link
            to="/contacto"
            className="inline-flex items-center gap-2 px-8 py-4 bg-primary-yellow text-deep-green font-bold rounded-lg hover:shadow-[0_0_25px_rgba(255,212,0,0.5)] hover:-translate-y-1 transition-all duration-300 whitespace-nowrap"
          >
            Consultar para {service.title}
            <i className="ri-arrow-right-line"></i>
          </Link>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default ServiciosPage;
